import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { palette } from '@m12s/component-library';

import MainLayout from 'components/Layouts/MainLayout';
import { Loading } from 'components/Loading';
import { AppNav } from 'components/AppNav';
import { HeaderLayout, HeaderText } from 'components/AppNav/styled';
import {
  formatMaterialQty,
  formatMaterialRatio,
} from 'lib/utils/pickingMaterials';

const Scroll = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${palette.Grey100};
  padding: 1.5rem;
  gap: 0;
`;

const ScanHint = styled.div`
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border: 1px dashed ${palette.Grey400};
  border-radius: 0.5rem;
  background: ${palette.White};
  color: ${palette.Grey600};
  font-size: 0.875rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-bottom: 1.5rem;

  & + & {
    padding-top: 1.5rem;
    border-top: 1px solid ${palette.Grey300};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
`;

const MaterialLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${palette.Grey700};
`;

const RequiredLabel = styled.span`
  font-size: 0.8125rem;
  color: ${palette.Grey500};
  white-space: nowrap;
`;

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  gap: 0.625rem;
`;

// Display-only by default: LPs are selected by scanning their barcode, not by
// tapping. When `clickable` (development-only manual selection) the tile renders
// as a button and reacts to taps.
const Tile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.5rem;
  border: 1.5px solid
    ${({ selected }) => {
      return selected ? palette.Indigo500 : palette.Grey300;
    }};
  background: ${({ selected }) => {
    return selected ? palette.Indigo50 : palette.White;
  }};
  text-align: left;

  ${({ clickable }) => {
    return clickable
      ? css`
          width: 100%;
          font: inherit;
          appearance: none;
          cursor: pointer;

          &:hover {
            border-color: ${palette.Indigo500};
          }
        `
      : '';
  }}
`;

const TileLp = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ selected }) => {
    return selected ? palette.Indigo600 : palette.Grey700;
  }};
`;

const TileQty = styled.span`
  font-size: 0.75rem;
  color: ${({ selected }) => {
    return selected ? palette.Indigo500 : palette.Grey500;
  }};
`;

const TileLoc = styled.span`
  font-size: 0.6875rem;
  color: ${({ selected }) => {
    return selected ? palette.Indigo500 : palette.Grey500;
  }};
`;

const Footer = styled.div`
  flex-shrink: 0;
  display: flex;
  border-top: 1px solid ${palette.Grey300};
  background: ${palette.White};
`;

const FooterBtn = styled.button`
  flex: 1;
  height: 3rem;
  border: none;
  background: none;
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${({ disabled }) => {
    return disabled ? palette.Grey400 : palette.Grey700;
  }};
  cursor: ${({ disabled }) => {
    return disabled ? 'not-allowed' : 'pointer';
  }};

  &:not(:first-child) {
    border-left: 1px solid ${palette.Grey300};
  }

  &:last-child {
    font-weight: 600;
    color: ${({ disabled }) => {
      return disabled ? palette.Grey400 : palette.Indigo600;
    }};
  }

  &:hover:not(:disabled) {
    background: ${palette.Grey50};
  }
`;

const TileSelected = styled.span`
  margin-top: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${palette.Indigo600};
`;

const LoadNotice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  color: ${palette.Grey600};
  font-size: 0.9375rem;
  text-align: center;
`;

const RetryButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${palette.Grey400};
  border-radius: 0.375rem;
  background: ${palette.White};
  color: ${palette.Grey700};
  font: inherit;
  font-size: 0.875rem;
  cursor: pointer;
`;

const NoLpsNotice = styled.div`
  padding: 0.875rem 1rem;
  border: 1px dashed ${palette.Grey400};
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  color: ${palette.Grey500};
  background: ${palette.White};
`;

const SelectBatchStep = ({
  materials,
  selections,
  onReset,
  onConfirm,
  onBack,
  onClose,
  manualSelectionEnabled,
  onToggleLp,
  loading,
  loadError,
  onRetry,
}) => {
  const hasAnySelection = materials.some((mat) => {
    const sel = selections[mat.itemNumber];
    return sel && sel.size > 0;
  });

  return (
    <MainLayout.Column id="select-batch-step">
      <AppNav onClose={onClose} onBack={onBack}>
        <HeaderLayout>
          <HeaderText semibold color={palette.White}>
            Select materials batch
          </HeaderText>
        </HeaderLayout>
      </AppNav>

      <MainLayout.Container direction="column" bgColor={palette.Grey100}>
        <Scroll>
          <ScanHint>
            {manualSelectionEnabled
              ? 'Dev mode: tap a license plate to select it, or scan its barcode.'
              : 'Scan a license plate barcode to add it. License plates cannot be selected manually.'}
          </ScanHint>
          {loading && (
            <Loading
              text="Loading materials"
              size="2.5rem"
              fontSize="0.9375rem"
            />
          )}
          {loadError && (
            <LoadNotice>
              <span>Materials could not be loaded.</span>
              <RetryButton type="button" onClick={onRetry}>
                Retry
              </RetryButton>
            </LoadNotice>
          )}
          {!loading &&
            !loadError &&
            materials.map((mat) => {
              const sel = selections[mat.itemNumber] || new Set();
              const ratio = formatMaterialRatio(mat.qtyPerUnit);
              return (
                <Section key={mat.itemNumber}>
                  <SectionHeader>
                    <MaterialLabel>
                      {mat.itemNumber}
                      {ratio ? ` | ${ratio}` : ''}
                    </MaterialLabel>
                    <RequiredLabel>
                      {mat.requiredQty} {mat.unit} required
                    </RequiredLabel>
                  </SectionHeader>
                  {mat.lps.length === 0 && (
                    <NoLpsNotice>
                      No license plates available for this material. Check
                      on-hand inventory in the ERP.
                    </NoLpsNotice>
                  )}
                  <TileGrid>
                    {mat.lps.map((lp) => {
                      const isSelected = sel.has(lp.lp);
                      const interactiveProps = manualSelectionEnabled
                        ? {
                            as: 'button',
                            type: 'button',
                            clickable: true,
                            'aria-pressed': isSelected,
                            onClick: () => {
                              return onToggleLp(mat.itemNumber, lp.lp);
                            },
                          }
                        : {};
                      return (
                        <Tile
                          key={lp.lp}
                          selected={isSelected}
                          {...interactiveProps}
                        >
                          <TileLp selected={isSelected}>{lp.lp}</TileLp>
                          <TileQty selected={isSelected}>
                            {formatMaterialQty(lp.available ?? lp.onHand)}{' '}
                            {mat.unit}
                          </TileQty>
                          {lp.location ? (
                            <TileLoc selected={isSelected}>
                              {lp.location}
                            </TileLoc>
                          ) : null}
                          {isSelected ? (
                            <TileSelected>✓ Selected</TileSelected>
                          ) : null}
                        </Tile>
                      );
                    })}
                  </TileGrid>
                </Section>
              );
            })}
        </Scroll>
      </MainLayout.Container>

      <Footer>
        <FooterBtn type="button" disabled={!hasAnySelection} onClick={onReset}>
          Reset selection
        </FooterBtn>
        <FooterBtn
          type="button"
          disabled={!hasAnySelection}
          onClick={onConfirm}
        >
          Confirm materials
        </FooterBtn>
      </Footer>
    </MainLayout.Column>
  );
};

SelectBatchStep.propTypes = {
  materials: PropTypes.arrayOf(
    PropTypes.shape({
      itemNumber: PropTypes.string,
      requiredQty: PropTypes.number,
      qtyPerUnit: PropTypes.number,
      unit: PropTypes.string,
      lps: PropTypes.array,
    })
  ).isRequired,
  selections: PropTypes.objectOf(PropTypes.instanceOf(Set)).isRequired,
  onReset: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  manualSelectionEnabled: PropTypes.bool,
  onToggleLp: PropTypes.func,
  loading: PropTypes.bool,
  loadError: PropTypes.bool,
  onRetry: PropTypes.func,
};

SelectBatchStep.defaultProps = {
  onBack: undefined,
  manualSelectionEnabled: false,
  onToggleLp: () => {},
  loading: false,
  loadError: false,
  onRetry: () => {},
};

export default SelectBatchStep;
